import * as React from 'react'

import {
  Box,
  Collapse,
  List,
  ListItem,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Typography,
  Divider,
  Paper,
} from '@mui/material'

import SmartToyOutlinedIcon from '@mui/icons-material/SmartToyOutlined';
import PeopleAltOutlinedIcon from '@mui/icons-material/PeopleAltOutlined';
import Diversity1OutlinedIcon from '@mui/icons-material/Diversity1Outlined';
import DescriptionOutlinedIcon from '@mui/icons-material/DescriptionOutlined';
import InsertChartOutlinedTwoToneIcon from '@mui/icons-material/InsertChartOutlinedTwoTone';
import MailOutlinedIcon from '@mui/icons-material/MailOutlined';
import HelpOutlineOutlinedIcon from '@mui/icons-material/HelpOutlineOutlined';
import SettingsOutlinedIcon from '@mui/icons-material/SettingsOutlined';
import VerifiedOutlinedIcon from '@mui/icons-material/VerifiedOutlined';
import TuneIcon from '@mui/icons-material/Tune';
import SchoolOutlinedIcon from '@mui/icons-material/SchoolOutlined';
import PsychologyOutlinedIcon from '@mui/icons-material/PsychologyOutlined';
import ExpandLess from '@mui/icons-material/ExpandLess';
import ExpandMore from '@mui/icons-material/ExpandMore';

function a11yProps(index) {
  return {
    id: `vertical-tab-${index}`,
    'aria-controls': `vertical-tabpanel-${index}`,
  };
}

function Sidebar(props) {
  const { value, onChange } = props;
  const [dataSourceOpen, setDataSourceOpen] = React.useState(false);
  const [knowledgebaseOpen, setKnowledgebaseOpen] = React.useState(false);

  const handleDataSourceClick = () => {
    setDataSourceOpen(!dataSourceOpen);
  };

  const handleKnowledgebaseClick = () => {
    setKnowledgebaseOpen(!knowledgebaseOpen);
  };

  const handleMenuItemClick = (tabValue) => {
    onChange(tabValue);
  };

  const menuItems = [
    { id: 0, icon: <InsertChartOutlinedTwoToneIcon />, label: "Dashboard" },
    { 
      id: 1, 
      icon: <DescriptionOutlinedIcon />, 
      label: "Data Source",
      hasSubmenu: true,
      submenuItems: [
        { id: 1, icon: <DescriptionOutlinedIcon />, label: "Data Source" },
        { id: 5, icon: <TuneIcon />, label: "Dimension Defaults" }
      ]
    },
    { 
      id: 9, 
      icon: <SchoolOutlinedIcon />, 
      label: "Knowledgebase",
      hasSubmenu: true,
      submenuItems: [
        { id: 10, icon: <PsychologyOutlinedIcon />, label: "Member Embeddings" }
      ]
    },
    { id: 2, icon: <PeopleAltOutlinedIcon />, label: "User" },
    { id: 3, icon: <Diversity1OutlinedIcon />, label: "Group" },
    { id: 4, icon: <VerifiedOutlinedIcon />, label: "Permission" },
    { id: 6, icon: <MailOutlinedIcon />, label: "Inbox" },
    { id: 7, icon: <HelpOutlineOutlinedIcon />, label: "Help" },
    { id: 8, icon: <SettingsOutlinedIcon />, label: "Setting" },
  ];

  return (
    <Paper
      elevation={0}
      sx={{ 
        width: 280,
        height: '100vh',
        borderRadius: 0,
        background: 'linear-gradient(180deg, #fafafa 0%, #f5f5f5 100%)',
        borderRight: '1px solid rgba(0, 0, 0, 0.08)',
        display: 'flex',
        flexDirection: 'column',
        overflow: 'hidden',
      }}
    >
      {/* Header Section */}
      <Box 
        sx={{
          display: 'flex',
          alignItems: 'center',
          padding: '24px 20px',
          background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
          color: 'white',
          position: 'relative',
          '&::after': {
            content: '""',
            position: 'absolute',
            bottom: 0,
            left: 0,
            right: 0,
            height: '1px',
            background: 'rgba(255, 255, 255, 0.2)',
          }
        }}
      >
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            gap: '12px',
            '& .MuiSvgIcon-root': {
              fontSize: '28px',
              filter: 'drop-shadow(0 2px 4px rgba(0,0,0,0.1))',
            }
          }}
        >
          <SmartToyOutlinedIcon />
          <Typography 
            variant="h6" 
            sx={{ 
              fontWeight: 600,
              fontSize: '1.3rem',
              letterSpacing: '-0.5px',
              textShadow: '0 1px 2px rgba(0,0,0,0.1)',
            }}
          >
            AnalyticChat
          </Typography>
        </Box>
      </Box>
      
      {/* Navigation Section */}
      <Box sx={{ flex: 1, overflow: 'hidden', padding: '16px 0' }}>
        <List component="nav" sx={{ padding: 0 }}>
          {menuItems.map((item, index) => (
            <React.Fragment key={item.id}>
              <ListItem disablePadding sx={{ marginBottom: '4px', paddingX: '12px' }}>
                <ListItemButton
                  onClick={() => {
                    if (item.hasSubmenu) {
                      if (item.id === 1) {
                        handleDataSourceClick();
                      } else if (item.id === 9) {
                        handleKnowledgebaseClick();
                      }
                    } else {
                      handleMenuItemClick(item.id);
                    }
                  }}
                  sx={{
                    minHeight: 52,
                    borderRadius: '12px',
                    paddingLeft: '20px',
                    paddingRight: '16px',
                    marginX: '8px',
                    gap: '12px',
                    color: value === item.id ? '#667eea' : '#64748b',
                    backgroundColor: value === item.id 
                      ? 'rgba(102, 126, 234, 0.12)' 
                      : 'transparent',
                    border: value === item.id 
                      ? '1px solid rgba(102, 126, 234, 0.2)' 
                      : '1px solid transparent',
                    transition: 'all 0.2s cubic-bezier(0.4, 0, 0.2, 1)',
                    position: 'relative',
                    overflow: 'hidden',
                    '&::before': value === item.id ? {
                      content: '""',
                      position: 'absolute',
                      left: 0,
                      top: 0,
                      bottom: 0,
                      width: '4px',
                      background: 'linear-gradient(180deg, #667eea 0%, #764ba2 100%)',
                      borderRadius: '0 2px 2px 0',
                    } : {},
                    '&:hover': {
                      backgroundColor: value === item.id 
                        ? 'rgba(102, 126, 234, 0.16)' 
                        : 'rgba(102, 126, 234, 0.08)',
                      transform: 'translateX(2px)',
                      boxShadow: '0 2px 8px rgba(102, 126, 234, 0.15)',
                    },
                    '&:active': {
                      transform: 'translateX(1px)',
                    }
                  }}
                >
                  <ListItemIcon 
                    sx={{ 
                      color: 'inherit', 
                      minWidth: 'auto', 
                      marginRight: 0,
                      '& .MuiSvgIcon-root': {
                        fontSize: '20px',
                        transition: 'transform 0.2s ease',
                      }
                    }}
                  >
                    {item.icon}
                  </ListItemIcon>
                  <ListItemText 
                    primary={item.label} 
                    sx={{ 
                      '& .MuiListItemText-primary': {
                        fontSize: '0.95rem',
                        fontWeight: value === item.id ? 600 : 500,
                        letterSpacing: '-0.2px',
                        transition: 'all 0.2s ease',
                      }
                    }}
                  />
                  {item.hasSubmenu && (
                    <Box
                      sx={{
                        transition: 'transform 0.2s ease',
                        transform: (item.id === 1 && dataSourceOpen) || (item.id === 9 && knowledgebaseOpen) ? 'rotate(180deg)' : 'rotate(0deg)',
                        '& .MuiSvgIcon-root': {
                          fontSize: '18px',
                          color: 'inherit',
                        }
                      }}
                    >
                      <ExpandMore />
                    </Box>
                  )}
                </ListItemButton>
              </ListItem>
              
              {item.hasSubmenu && (
                <Collapse 
                  in={(item.id === 1 && dataSourceOpen) || (item.id === 9 && knowledgebaseOpen)} 
                  timeout="auto" 
                  unmountOnExit
                  sx={{
                    '& .MuiCollapse-wrapper': {
                      paddingLeft: '20px',
                    }
                  }}
                >
                  <List component="div" disablePadding>
                    {item.submenuItems.map((subItem) => (
                      <ListItem key={subItem.id} disablePadding sx={{ marginBottom: '2px', paddingX: '12px' }}>
                        <ListItemButton
                          onClick={() => handleMenuItemClick(subItem.id)}
                          sx={{
                            minHeight: 44,
                            borderRadius: '10px',
                            paddingLeft: '32px',
                            paddingRight: '16px',
                            marginX: '8px',
                            gap: '12px',
                            color: value === subItem.id ? '#667eea' : '#64748b',
                            backgroundColor: value === subItem.id 
                              ? 'rgba(102, 126, 234, 0.12)' 
                              : 'transparent',
                            border: value === subItem.id 
                              ? '1px solid rgba(102, 126, 234, 0.2)' 
                              : '1px solid transparent',
                            transition: 'all 0.2s cubic-bezier(0.4, 0, 0.2, 1)',
                            position: 'relative',
                            '&::before': value === subItem.id ? {
                              content: '""',
                              position: 'absolute',
                              left: 0,
                              top: 0,
                              bottom: 0,
                              width: '3px',
                              background: 'linear-gradient(180deg, #667eea 0%, #764ba2 100%)',
                              borderRadius: '0 2px 2px 0',
                            } : {},
                            '&:hover': {
                              backgroundColor: value === subItem.id 
                                ? 'rgba(102, 126, 234, 0.16)' 
                                : 'rgba(102, 126, 234, 0.08)',
                              transform: 'translateX(2px)',
                              boxShadow: '0 2px 8px rgba(102, 126, 234, 0.15)',
                            },
                            '&:active': {
                              transform: 'translateX(1px)',
                            }
                          }}
                        >
                          <ListItemIcon 
                            sx={{ 
                              color: 'inherit', 
                              minWidth: 'auto', 
                              marginRight: 0,
                              '& .MuiSvgIcon-root': {
                                fontSize: '18px',
                                transition: 'transform 0.2s ease',
                              }
                            }}
                          >
                            {subItem.icon}
                          </ListItemIcon>
                          <ListItemText 
                            primary={subItem.label}
                            sx={{ 
                              '& .MuiListItemText-primary': {
                                fontSize: '0.9rem',
                                fontWeight: value === subItem.id ? 600 : 500,
                                letterSpacing: '-0.2px',
                                transition: 'all 0.2s ease',
                              }
                            }}
                          />
                        </ListItemButton>
                      </ListItem>
                    ))}
                  </List>
                </Collapse>
              )}
            </React.Fragment>
          ))}
        </List>
      </Box>
      
      {/* Footer Section */}
      <Box
        sx={{
          padding: '16px 20px',
          borderTop: '1px solid rgba(0, 0, 0, 0.08)',
          background: 'rgba(255, 255, 255, 0.5)',
        }}
      >
        <Typography
          variant="caption"
          sx={{
            color: '#94a3b8',
            fontSize: '0.75rem',
            textAlign: 'center',
            display: 'block',
            fontWeight: 500,
          }}
        >
          v1.0.0 • AnalyticChat
        </Typography>
      </Box>
    </Paper>
  );
}

export default Sidebar;